import React, { useEffect, useMemo, useState } from 'react';
import { Platform, Pressable, StyleSheet, useWindowDimensions, View } from 'react-native';
import { useThemeSettings } from '../../../ThemeProvider';
import getGlobalStyles from '../../../globalStyles';
import CustomLinearGradient, { GRADIENT_TYPE } from '../../../components/CustomLinearGradient';
import InstallAppPromptModal from './InstallAppPromptModal';

const DISMISS_KEY = 'thinko.installPrompt.dismissed';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.thinko.thinkoapp';
const APP_STORE_URL = 'https://apps.apple.com/app/id123456789';

type StoreTarget = { url: string } | null;

const detectStore = (): StoreTarget => {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/Android/i.test(ua)) return { url: PLAY_STORE_URL };
  if (/iPhone|iPad|iPod/i.test(ua)) return { url: APP_STORE_URL };
  return { url: PLAY_STORE_URL };
};

const InstallAppPrompt = () => {
  const { theme } = useThemeSettings();
  const { width } = useWindowDimensions();
  const gStyles = useMemo(() => getGlobalStyles(theme), [theme]);
  const [visible, setVisible] = useState(false);
  const [storeUrl, setStoreUrl] = useState<string | undefined>(undefined);
  const isMobileViewport = width <= 768;

  useEffect(() => {
    if (Platform.OS !== 'web') return;
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('src') !== 'share') return;

    // remove src so a refresh/share-back doesn't re-trigger the prompt
    params.delete('src');
    const query = params.toString();
    const cleanUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', cleanUrl);

    if (window.localStorage?.getItem(DISMISS_KEY)) return;

    const store = detectStore();
    setStoreUrl(store?.url);
    setVisible(true);
  }, []);

  if (!visible || Platform.OS !== 'web') return null;

  return (
    <View
      style={[
        gStyles.backdrop,
        styles.overlay,
        styles.strongOverlay,
        { justifyContent: isMobileViewport ? 'flex-end' : 'center' },
      ]}
      pointerEvents="auto">
      <Pressable onPress={() => setVisible(false)} style={styles.scrimTapArea} />
      <CustomLinearGradient
        gradientType={GRADIENT_TYPE.MODAL}
        style={[
          styles.modalShell,
          gStyles.gradientBorderShadow,
          isMobileViewport ? styles.modalShellMobile : styles.modalShellDesktop,
        ]}>
        <InstallAppPromptModal
          storeUrl={storeUrl}
          onContinue={() => {
            try { window.localStorage?.setItem(DISMISS_KEY, '1'); } catch {}
            setVisible(false);
          }}
        />
      </CustomLinearGradient>
    </View>
  );
};

export default InstallAppPrompt;

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    zIndex: 2000,
  },
  strongOverlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
  },
  scrimTapArea: {
    ...StyleSheet.absoluteFillObject,
  },
  modalShell: {
    width: '92%',
    maxWidth: 420,
    borderRadius: 20,
  },
  modalShellDesktop: {
    marginTop: 0,
  },
  modalShellMobile: {
    width: '100%',
    maxWidth: 420,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    marginBottom: 0,
  },
});
